import { IProfile } from "../../../models/profile.model";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import MainLayout from "../../layouts/main-layout/MainLayout";
import "./ItemDatabasePage.scss";
import TitleBox from "../../ui/title-box/TitleBox";
import ProfileCard from "../profile-manager-page/components/profile-card/ProfileCard";
import TabList from "../../ui/tab-list/TabList";
import Tab from "../../ui/tab-list/components/tab/Tab";
import ItemList from "./components/item-list/ItemList";
import ItemType from "../../../enums/itemType.enum";
import { gameActions } from "../../../store/gameSlice";
import { ItemStack } from "../../../models/itemStack.model";
import { Fragment, useEffect, useState } from "react";
import ActionIcon from "../../ui/action-icon/ActionIcon";
import { profileActions } from "../../../store/profileSlice";
import { socketActions } from "../../../store/socketSlice";

const ItemDatabasePage = () => {
  const selectedClasses = useAppSelector((s) => s.game.selectedClasses);
  const weaponClasses = useAppSelector((s) => s.game.weaponClasses);
  const magazineClasses = useAppSelector((s) => s.game.magazineClasses);
  const backpackClasses = useAppSelector((s) => s.game.backpackClasses);
  const dispatch = useAppDispatch();

  const [filter, setFilter] = useState("");

  const [weaponsFiltered, setWeaponsFiltered] = useState<string[]>([]);
  const [magazinesFiltered, setMagazinesFiltered] = useState<string[]>([]);
  const [backpacksFiltered, setBackpacksFiltered] = useState<string[]>([]);

  useEffect(() => {
    filterItems();
  }, [filter, weaponClasses, magazineClasses, backpackClasses]);

  const filterItems = () => {
    setWeaponsFiltered(
      weaponClasses.filter((w) =>
        w.toLowerCase().includes(filter.toLowerCase())
      )
    );
    setBackpacksFiltered(
      backpackClasses.filter((b) =>
        b.toLowerCase().includes(filter.toLowerCase())
      )
    );
    setMagazinesFiltered(
      magazineClasses.filter((m) =>
        m.toLowerCase().includes(filter.toLowerCase())
      )
    );
  };

  const selectionProfile: IProfile = {
    name: "Current selection",
    items: [
      ...selectedClasses.weapons,
      ...selectedClasses.magazines,
      ...selectedClasses.backpacks,
    ],
  };

  const updateWeaponSelection = (classname: string, addAmount: number) => {
    const stack: ItemStack = {
      classname,
      type: ItemType.Weapon,
      amount: addAmount,
    };
    dispatch(gameActions.addWeaponSelectionItemStack(stack));
  };

  const updateMagazineSelection = (classname: string, addAmount: number) => {
    const stack: ItemStack = {
      classname,
      type: ItemType.Magazine,
      amount: addAmount,
    };
    dispatch(gameActions.addMagazineSelectionItemStack(stack));
  };

  const updateBackpackSelection = (classname: string, addAmount: number) => {
    const stack: ItemStack = {
      classname,
      type: ItemType.Backpack,
      amount: addAmount,
    };
    dispatch(gameActions.addBackpackSelectionItemStack(stack));
  };

  const clearSelection = () => dispatch(gameActions.clearSelection());

  const saveSelection = () => {
    const profileName = window.prompt("Please enter a profile name");
    if (profileName && profileName.length > 0) {
      const profile: IProfile = {
        name: profileName,
        items: [
          ...selectedClasses.weapons,
          ...selectedClasses.magazines,
          ...selectedClasses.backpacks,
        ],
      };

      dispatch(profileActions.addProfile(profile));
      dispatch(gameActions.clearSelection());
    }
  };

  const spawnSelection = () => {
    const spawnCommands: string[] = [];

    [...selectedClasses.weapons, ...selectedClasses.magazines, ...selectedClasses.backpacks].forEach(stack => {
      const cmd = stack.type === ItemType.Weapon ? "spawnWeapon" : stack.type === ItemType.Magazine ? "spawnMagazine" : stack.type === ItemType.Backpack ? "spawnBackpack" : undefined;

      if (cmd !== undefined) {
        spawnCommands.push(`${cmd} ${stack.classname} ${stack.amount};`);
      }
    });

    if (spawnCommands.length > 0) {
      dispatch(socketActions.sendMessage(spawnCommands.join("\n")));
    } else {
      console.log("no valid spawn commands found");
    }
  };

  return (
    <MainLayout>
      <div className="item-database-page">
        <TitleBox title="Item Database" />
        <div className="search-bar">
          <input
            type="text"
            placeholder="enter search pattern to filter the items below..."
            className="input-field"
            value={filter ?? ""}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div className="database-wrapper">
          <div className="selection-area">
            <ProfileCard profile={selectionProfile}>
              {selectionProfile.items.length > 0 && (
                <Fragment>
                  <ActionIcon
                    icon="fa-solid fa-circle-play"
                    onClick={spawnSelection}
                  />
                  <ActionIcon
                    icon="fa-solid fa-floppy-disk"
                    onClick={saveSelection}
                  />
                  <ActionIcon
                    icon="fa-solid fa-trash-can"
                    onClick={clearSelection}
                  />
                </Fragment>
              )}
            </ProfileCard>
          </div>
          <div className="database-area">
            <TabList>
              <Tab
                title={`Weapons (${weaponsFiltered.length})`}
                icon="fa-solid fa-gun"
              >
                <ItemList
                  items={weaponsFiltered}
                  title="Weapons"
                  onIncrement={(classname) =>
                    updateWeaponSelection(classname, 1)
                  }
                  onDecrement={(classname) =>
                    updateWeaponSelection(classname, -1)
                  }
                />
              </Tab>
              <Tab
                title={`Magazines (${magazinesFiltered.length})`}
                icon="fa-solid  fa-shield"
              >
                <ItemList
                  items={magazinesFiltered}
                  title="Magazines"
                  onIncrement={(classname) =>
                    updateMagazineSelection(classname, 1)
                  }
                  onDecrement={(classname) =>
                    updateMagazineSelection(classname, -1)
                  }
                />
              </Tab>
              <Tab
                title={`Backpacks (${backpacksFiltered.length})`}
                icon="fa-solid fa-briefcase"
              >
                <ItemList
                  items={backpacksFiltered}
                  title="Backpacks"
                  onIncrement={(classname) =>
                    updateBackpackSelection(classname, 1)
                  }
                  onDecrement={(classname) =>
                    updateBackpackSelection(classname, -1)
                  }
                />
              </Tab>
            </TabList>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ItemDatabasePage;
